import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtNum, fmtDateTime, fmtMoney, fmtPct, monthLabel } from "@/lib/format";
import { PageHeader, SectionTitle, AwaitingData } from "@/components/common/Page";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { FormDialog } from "@/components/common/FormDialog";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, Trash2, Info, Eye } from "lucide-react";
import { useProducts, useCustomers, productOptions, customerOptions, nameOf, opt } from "@/lib/lookups";

const METRICS = ["vessels", "registered_users", "active_users", "database_storage_gb", "file_storage_gb", "backup_storage_gb", "api_requests", "ai_requests", "ai_tokens", "emails", "documents_generated", "gps_records", "gps_storage_gb", "data_transfer_gb"];
const metricLabel = (m) => m.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Usage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const { data: rows } = useQuery({ queryKey: ["usage"], queryFn: async () => (await api.get("/usage")).data });
  const { data: products } = useProducts();
  const { data: customers } = useCustomers();

  const save = useMutation({
    mutationFn: async (p) => {
      const metrics = {};
      METRICS.forEach((m) => { if (p[m] !== "" && p[m] != null && Number(p[m]) !== 0) metrics[m] = Number(p[m]); });
      return api.post("/usage", {
        product_id: p.product_id, customer_id: p.customer_id || null, tenant_id: p.tenant_id || null,
        product_instance_id: p.product_instance_id || null, period: p.period,
        environment: p.environment, source: "manual", metrics,
      });
    },
    onSuccess: () => { qc.invalidateQueries(); toast.success("Usage recorded"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({ mutationFn: async (id) => api.delete(`/usage/${id}`), onSuccess: () => { qc.invalidateQueries(); setDetail(null); toast.success("Usage record removed"); } });

  const fields = [
    { name: "product_id", label: "Product", type: "select", options: productOptions(products, false) },
    { name: "product_instance_id", label: "Product Instance", placeholder: "Customer A MarineOS Prod" },
    { name: "customer_id", label: "Customer", type: "select", options: customerOptions(customers) },
    { name: "tenant_id", label: "Tenant / External ID" },
    { name: "period", label: "Reporting Period (YYYY-MM)", placeholder: "2026-06" },
    { name: "environment", label: "Environment", type: "select", options: opt(["production", "staging", "development"]), default: "production" },
    ...METRICS.map((m) => ({ name: m, label: metricLabel(m), type: "number", step: "0.01" })),
  ];

  const columns = [
    { key: "period", header: "Period" },
    { key: "product_id", header: "Product", render: (r) => nameOf(products, r.product_id) },
    { key: "product_instance_id", header: "Instance", render: (r) => r.product_instance_id || "—" },
    { key: "customer_id", header: "Customer", render: (r) => nameOf(customers, r.customer_id) },
    { key: "mapping_status", header: "Mapping", render: (r) => <StatusBadge status={r.mapping_status || (r.customer_id ? "mapped" : "manual")} testId={`usage-mapping-${r.id}`} /> },
    { key: "environment", header: "Env", render: (r) => <span className={`text-xs capitalize ${r.environment === "production" ? "text-slate-300" : "text-amber-400"}`}>{r.environment}</span> },
    { key: "source", header: "Source", render: (r) => <StatusBadge status={r.source === "manual" ? "not_connected" : "connected"} label={r.source === "manual" ? "Manual" : r.source} /> },
    { key: "received_at", header: "Received", render: (r) => <span className="text-xs text-slate-500">{fmtDateTime(r.received_at || r.updated_at)}</span> },
    { key: "actions", header: "", align: "right", render: (r) => (
      <div className="flex justify-end gap-1">
        <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs text-cyan-400 hover:text-cyan-300" onClick={(e) => { e.stopPropagation(); setDetail(r); }} data-testid={`view-usage-${r.id}`}><Eye className="h-3.5 w-3.5" /> View</Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-rose-400" onClick={(e) => { e.stopPropagation(); del.mutate(r.id); }} data-testid={`delete-usage-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    ) },
  ];

  return (
    <div className="ns-fade">
      <PageHeader title="Product Usage" subtitle="Aggregated usage from products — manual entry until integrations connect" testId="usage-header">
        <Button onClick={() => setOpen(true)} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500" data-testid="add-usage-button">
          <Plus className="mr-1.5 h-4 w-4" /> Add Usage
        </Button>
      </PageHeader>
      <div className="mb-4 flex items-start gap-2 rounded-lg border border-[#1E2D4A] bg-[#111A2E]/60 px-4 py-3 text-sm text-slate-400">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
        Records associate Product → Instance → Customer → Tenant/External ID → Period → Environment → Source. Only <span className="mx-1 text-slate-200">production</span> data flows into financial reports; staging/development stays isolated. Automated values (idempotency-keyed) supplement manual entries without duplicates.
      </div>
      <DataTable columns={columns} rows={rows} testId="usage-table" onRowClick={(r) => setDetail(r)} empty={{ title: "No usage records", message: "Add manual usage or connect a product integration to receive it automatically." }} />
      <FormDialog open={open} onOpenChange={setOpen} title="Add Usage Record" fields={fields} onSubmit={(p) => save.mutateAsync(p).then(() => true).catch(() => false)} testId="usage-form" />
      <UsageDetail record={detail} onClose={() => setDetail(null)} products={products} customers={customers} />
    </div>
  );
}

function UsageDetail({ record, onClose, products, customers }) {
  const { data: profit } = useQuery({
    queryKey: ["usage-cost", record?.customer_id, record?.period],
    enabled: !!record?.customer_id,
    queryFn: async () => (await api.get(`/profitability/customers/${record.customer_id}?month=${record.period}`)).data,
  });
  if (!record) return null;
  const metrics = record.metrics || {};
  const metricKeys = Object.keys(metrics);

  const Row = ({ l, v }) => (
    <div className="flex items-center justify-between border-b border-[#1E2D4A] py-2 last:border-0">
      <span className="text-xs text-slate-500">{l}</span>
      <span className="text-sm text-slate-200">{v || "—"}</span>
    </div>
  );

  return (
    <Sheet open={!!record} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-md" data-testid="usage-detail-drawer">
        <SheetHeader>
          <SheetTitle className="text-slate-100">Usage Record · {record.period}</SheetTitle>
        </SheetHeader>

        <SectionTitle className="mb-2 mt-6">Association</SectionTitle>
        <div className="rounded-lg border border-[#1E2D4A] bg-[#17233B]/50 px-3">
          <Row l="Product" v={nameOf(products, record.product_id)} />
          <Row l="Product Instance" v={record.product_instance_id} />
          <Row l="Customer (NavScora)" v={nameOf(customers, record.customer_id)} />
          <Row l="External Customer ID" v={record.external_customer_id} />
          <Row l="Tenant / External ID" v={record.tenant_id} />
          <Row l="Reporting Period" v={monthLabel(record.period)} />
          <Row l="Environment" v={<span className="capitalize">{record.environment}</span>} />
          <Row l="Received" v={fmtDateTime(record.received_at || record.updated_at)} />
        </div>

        <div className="mt-3 flex items-center justify-between">
          <SectionTitle>Source</SectionTitle>
          <StatusBadge status={record.source === "manual" ? "not_connected" : "connected"} label={record.source === "manual" ? "Manual entry" : record.source} />
        </div>
        {record.environment !== "production" && (
          <p className="mt-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-300">
            Non-production ({record.environment}) — excluded from production financial reports.
          </p>
        )}
        {record.mapping_status === "needs_review" && (
          <p className="mt-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-300" data-testid="usage-needs-review-note">
            Needs Review — tenant/customer not mapped to a NavScora customer. Excluded from finance, cost-to-serve and profitability until an admin adds a mapping under Integrations → Mappings.
          </p>
        )}

        <SectionTitle className="mb-2 mt-6">Metrics</SectionTitle>
        {metricKeys.length === 0 ? (
          <AwaitingData label="No metrics on this record" />
        ) : (
          <div className="rounded-lg border border-[#1E2D4A] bg-[#17233B]/50 px-3">
            {metricKeys.map((k) => (
              <div key={k} className="flex items-center justify-between border-b border-[#1E2D4A] py-2 last:border-0">
                <span className="text-xs text-slate-400">{metricLabel(k)}</span>
                <span className="font-mono-fin text-sm text-slate-100">{fmtNum(metrics[k])}</span>
              </div>
            ))}
          </div>
        )}

        <SectionTitle className="mb-2 mt-6">Calculated Costs</SectionTitle>
        {!record.customer_id ? (
          <AwaitingData label="Not linked to a customer — Insufficient data" />
        ) : !profit ? (
          <AwaitingData label="Calculating…" />
        ) : profit.cost_components?.length === 0 ? (
          <AwaitingData label="No costs allocated for this customer/period" />
        ) : (
          <div className="space-y-2">
            {profit.cost_components.map((c, i) => (
              <div key={i} className="flex items-start justify-between rounded-lg border border-[#1E2D4A] bg-[#17233B]/50 p-2.5">
                <div><div className="text-xs text-slate-200">{c.label}</div><div className="text-[10px] text-slate-500">Source: {c.source}</div></div>
                <span className="font-mono-fin text-sm text-slate-100">{fmtMoney(c.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between border-t border-[#1E2D4A] px-2.5 pt-2 text-sm">
              <span className="text-slate-400">Cost to serve</span><span className="font-mono-fin font-semibold text-slate-100">{fmtMoney(profit.cost_to_serve)}</span>
            </div>
            <div className="flex justify-between px-2.5 text-sm">
              <span className="text-slate-400">Margin</span><span className="font-mono-fin text-slate-100">{fmtPct(profit.margin_pct)}</span>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
